import { DispatchWithoutAction, useEffect, useState } from "react";

import { BusinessUnit } from "../../../Models/BusinessUnit";
import { Center } from "../../../Models/Center";
import { CustomDimension } from "../../../Models/CustomDimension";
import { DisqualifyingReason } from "../../../Models/DisqualifyingReason";
import { DynamicContent } from "../../../Models/DynamicContent";
import { Error } from "../../../Utils/Errors";
import { useApi } from "../../../Utils/useApi";
import { AreaOfInterest } from "../../Playbook/Interests/Types/AreaOfInterest";
import { LoginDependencies, PendingLoginDependencies } from "../Types/LoginDependencies";

interface UseGetLoginDependenciesResult {
  /** The fully formed list of application dependencies. */
  dependencies: LoginDependencies | null;
  /** Dispatches the api request. */
  execute: DispatchWithoutAction;
}

const useGetLoginDependencies = (): UseGetLoginDependenciesResult => {
  const [valid, setValid] = useState(false);
  const [dependencies, setDependencies] = useState<LoginDependencies | null>(null);
  const [pendingDependencies, setPendingDependencies] = useState(new PendingLoginDependencies());

  const { response: areasOfInterest } = useApi<AreaOfInterest[]>({
    url: "Legacy/V2/AreasOfInterest",
    error: Error.GetAreasOfInterest,
    valid
  });
  const { response: businessUnits } = useApi<BusinessUnit[]>({
    url: "Legacy/V0/businessUnits",
    error: Error.GetBusinessUnits,
    valid
  });
  const { response: centers } = useApi<Center[]>({
    url: "Legacy/V1/Centers",
    error: Error.GetCenters,
    valid
  });
  const { response: disqualifyingReasons } = useApi<DisqualifyingReason[]>({
    url: "Legacy/V0/Contacts/DisqualifyingReasons",
    error: Error.GetDisqualifyingReason,
    valid
  });
  const { response: dynamicContent } = useApi<DynamicContent[]>({
    url: "Legacy/V0/DynamicContent",
    error: Error.GetDynamicContent,
    valid
  });
  const { response: leadResultTypes } = useApi<CustomDimension<number>[]>({
    url: "Legacy/V0/Contacts/ContactResultTypes",
    error: Error.GetLeadResultTypes,
    valid
  });
  const { response: notOfferedProcedures } = useApi<CustomDimension<string>[]>({
    url: "Legacy/V0/Contacts/NotOfferedProcedures",
    error: Error.GetNotOfferedProcedures,
    valid
  });

  useEffect(() => {
    if (areasOfInterest) setPendingDependencies(rsx => ({ ...rsx, areasOfInterest }));
  }, [areasOfInterest, setPendingDependencies]);
  useEffect(() => {
    if (businessUnits) setPendingDependencies(rsx => ({ ...rsx, businessUnits }));
  }, [businessUnits, setPendingDependencies]);
  useEffect(() => {
    if (centers) setPendingDependencies(rsx => ({ ...rsx, centers }));
  }, [centers, setPendingDependencies]);
  useEffect(() => {
    if (disqualifyingReasons) setPendingDependencies(rsx => ({ ...rsx, disqualifyingReasons }));
  }, [disqualifyingReasons, setPendingDependencies]);
  useEffect(() => {
    if (dynamicContent) setPendingDependencies(rsx => ({ ...rsx, dynamicContent }));
  }, [dynamicContent, setPendingDependencies]);
  useEffect(() => {
    if (leadResultTypes) setPendingDependencies(rsx => ({ ...rsx, leadResultTypes }));
  }, [leadResultTypes, setPendingDependencies]);
  useEffect(() => {
    if (notOfferedProcedures) setPendingDependencies(rsx => ({ ...rsx, notOfferedProcedures }));
  }, [notOfferedProcedures, setPendingDependencies]);

  useEffect(() => {
    const completedDependencies = PendingLoginDependencies.GetComplete(pendingDependencies);
    if (completedDependencies) setDependencies(completedDependencies);
  }, [pendingDependencies]);

  return { dependencies, execute: () => setValid(true) };
};

export default useGetLoginDependencies;
